import {
  ISlider,
  IField,
  IValue,
  IFormDocument,
} from 'app/services/api5-service/api.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';

import { SliderSettings, SliderProps } from './model/slider-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';

interface SliderDataValue extends IValue {
  $max: string;
  $min: string;
  $step: string;
}

/**
 * Get data for slider
 *
 * @param control
 * @param doc
 */
function getSliderData(control: ISlider, doc: IFormDocument): SliderSettings {
  const data: SliderSettings = {
    max: Number(control.$max),
    min: Number(control.$min),
    step: Number(control.$step),
  };

  const dataValue = doc[control.$name] as SliderDataValue | undefined;
  if (dataValue?.$max) {
    data.max = Number(dataValue.$max);
  }
  if (dataValue?.$min) {
    data.min = Number(dataValue.$min);
  }
  if (dataValue?.$step) {
    data.step = Number(dataValue.$step);
  }

  return data;
}

/**
 * Get stream of data for slider
 *
 * @param control
 * @param state
 */
function getSliderData$(
  control: ISlider,
  state: ISPFormState,
): Observable<SliderSettings> {
  return state.docSubject.pipe(map(doc => getSliderData(control, doc)));
}

/**
 * Get props for slider field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getSliderProps(
  control: ISlider,
  field: IField,
  state: ISPFormState,
): SliderProps {
  return {
    originalControl: control,
    originalField: field,
    fieldSettings$: of({
      displayMode: 'input-like',
      tagPlace: 'field',
    } as FieldSettings),
    data$: getSliderData$(control, state),
  };
}

/**
 * Get config for slider field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getSliderConfig(
  control: ISlider,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Slider> {
  const props = getSliderProps(control, field, state);

  const config: ISPFieldConfig<ISPFieldType.Slider> = {
    key: control.$name,
    type: ISPFieldType.Slider,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
    props,
    modelOptions: {
      updateOn: 'change',
      debounce: {
        default: 1000,
      },
    },
  };

  return config;
}
